import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, EmptyState, IconButton, Page, PageHeader, Pagination, SearchInput, Segmented, TableSkeleton } from "../../../components/ui";
import { getComeback } from "../../../services/comeback";
import { getUser } from "../../../services/users";
import { getUserInfo } from "../../../store/actions/uploadMediaAction";
import { can } from "../../../utils/roles";
import { formatMoney } from "../payments-ledger/shared";
import ComebackComposer from "./ComebackComposer";
import "../payments-ledger/payments.css";

/** Situation d'aujourd'hui de la personne (jamais cachée de la liste). */
const StateBadge = ({ item }) => {
  const { t } = useTranslation();
  if (item.state === "unpaid") return <Badge color="amber">{t("Unpaid (card refused)")}</Badge>;
  if (item.state === "gifted") return <Badge color="blue">{t("Offered days until {{date}}", { date: day(item.accessUntil) })}</Badge>;
  if (item.state === "returned" && item.renewedAtStripe) {
    return (
      <span title={t("Stripe renewed this subscription but our database had missed it: this person never left.")}>
        <Badge color="green">{t("Renewed at Stripe (never left)")}</Badge>
      </span>
    );
  }
  if (item.state === "returned") return <Badge color="green">{t("Subscribed again")}</Badge>;
  return null;
};

const day = (date) => (date ? new Date(date).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) : "—");

/**
 * Paying subscribers who left (subscription ended, not renewed) or are about
 * to (cancellation scheduled), with their plan, reason and what they paid;
 * comeback offer by email, with free Premium days if wanted.
 */
const Comeback = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const canEdit = can("users", "edit");
  const [status, setStatus] = useState("ended");
  const [days, setDays] = useState(90);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState([]);
  const [composer, setComposer] = useState(null);

  const load = useCallback(() => {
    setData(null);
    setError(false);
    getComeback({ status, days, page, q: search.trim() })
      .then(setData)
      .catch(() => setError(true));
  }, [status, days, page, search]);
  useEffect(() => {
    const timer = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const reset = (setter) => (value) => {
    setter(value);
    setPage(1);
    setSelected([]);
  };
  const items = data?.items || [];
  const selectable = items.filter((item) => item.user.reachable && item.state !== "returned");
  const allSelected = selectable.length > 0 && selectable.every((item) => selected.includes(item.subscriptionId));
  const toggle = (id) => setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  const openUser = (id) =>
    getUser(id).then((user) => {
      dispatch(getUserInfo(user));
      history.push("/dashboard/user-info");
    });

  return (
    <Page>
      <PageHeader
        title={t("Subscribers to win back")}
        subtitle={t("Paying subscribers who left or are about to leave: why, what they paid, and a comeback offer.")}
        actions={<IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={load} />}
      />

      <div className="ledger-stats">
        <button type="button" className="ui-card ledger-stat" onClick={() => reset(setStatus)("ended")}>
          <strong>{data ? data.counts.ended : "—"}</strong>
          <span>{t("Left over the period")}</span>
        </button>
        <button type="button" className="ui-card ledger-stat" data-tone="amber" onClick={() => reset(setStatus)("cancelling")}>
          <strong>{data ? data.counts.cancelling : "—"}</strong>
          <span>{t("Cancellation scheduled")}</span>
        </button>
        <button type="button" className="ui-card ledger-stat" data-tone="green" onClick={() => reset(setStatus)("returned")}>
          <strong>{data ? data.counts.returned ?? 0 : "—"}</strong>
          <span>{t("Came back")}</span>
        </button>
      </div>

      <section className="ui-card">
        <div className="ui-toolbar ledger-toolbar">
          <Segmented
            label={t("status")}
            value={status}
            onChange={reset(setStatus)}
            options={[
              { value: "ended", label: t("Left") },
              { value: "cancelling", label: t("About to leave") },
              { value: "returned", label: t("Came back") },
              { value: "all", label: t("All") },
            ]}
          />
          {status !== "cancelling" && (
            <Segmented
              label={t("Period")}
              value={days}
              onChange={reset(setDays)}
              options={[30, 90, 180, 365].map((value) => ({ value, label: value === 365 ? t("1 year") : t("{{count}} days", { count: value }) }))}
            />
          )}
          <SearchInput value={search} onChange={reset(setSearch)} placeholder={t("Name, email or phone")} />
        </div>

        {canEdit && selected.length > 0 && (
          <div className="ledger-selection" role="status">
            <span>{t("selectedCount", { count: selected.length })}</span>
            <button type="button" className="ui-btn" onClick={() => setComposer(items.filter((item) => selected.includes(item.subscriptionId)))}>
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              {t("Send a comeback offer")}
            </button>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setSelected([])}>
              {t("Cancel")}
            </button>
          </div>
        )}

        {error ? (
          <EmptyState icon="ri-error-warning-line">
            {t("The list could not be loaded.")}{" "}
            <button type="button" className="ui-btn ui-btn-secondary" onClick={load}>
              {t("Retry")}
            </button>
          </EmptyState>
        ) : !data ? (
          <TableSkeleton />
        ) : !items.length ? (
          <EmptyState icon="ri-user-received-line">{status === "cancelling" ? t("No cancellation scheduled") : t("Nobody left over this period")}</EmptyState>
        ) : (
          <div className="ui-table-wrap">
            <table className="ui-table">
              <thead>
                <tr>
                  {canEdit && (
                    <th>
                      <input type="checkbox" aria-label={t("Select all")} checked={allSelected} onChange={() => setSelected(allSelected ? [] : selectable.map((item) => item.subscriptionId))} />
                    </th>
                  )}
                  <th>{t("Subscriber")}</th>
                  <th>{t("Plan")}</th>
                  <th>{t("End")}</th>
                  <th>{t("Reason given")}</th>
                  <th>{t("Paid")}</th>
                  <th>{t("Last offer")}</th>
                  {canEdit && <th></th>}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.subscriptionId} className="ledger-row" onClick={() => openUser(item.user.id)}>
                    {canEdit && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={t("Select")}
                          disabled={!item.user.reachable || item.state === "returned"}
                          title={item.user.blocked || undefined}
                          checked={selected.includes(item.subscriptionId)}
                          onChange={() => toggle(item.subscriptionId)}
                        />
                      </td>
                    )}
                    <td>
                      <span className="ui-media-title">
                        {item.user.name} <StateBadge item={item} />
                      </span>
                      <span className="ui-media-meta">
                        {[item.user.email, item.user.country].filter(Boolean).join(" · ")}
                        {item.user.blocked && <Badge color="slate">{t(item.user.blocked)}</Badge>}
                      </span>
                    </td>
                    <td>
                      {item.plan === "annual" ? t("Annual") : t("Monthly")}
                      <span className="ui-media-meta">{item.method === "mobile" ? t("Mobile Money") : t("Card")}</span>
                    </td>
                    <td>
                      {item.state === "cancelling" ? <Badge color="amber">{t("Ends {{date}}", { date: day(item.end) })}</Badge> : day(item.end)}
                    </td>
                    <td className="ui-cell-muted">{item.reason || "—"}</td>
                    <td>{item.paid.length ? item.paid.map((p) => formatMoney(p.total, p.currency)).join(" + ") : "—"}</td>
                    <td className="ui-cell-muted">{item.lastOfferAt ? `${day(item.lastOfferAt)}${item.offers > 1 ? ` (${item.offers})` : ""}` : t("Never")}</td>
                    {canEdit && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="ui-btn ui-btn-secondary" disabled={!item.user.reachable || item.state === "returned"} aria-label={t("Comeback offer to {{name}}", { name: item.user.name })} onClick={() => setComposer([item])}>
                          <i className="ri-mail-send-line" aria-hidden="true"></i>
                          {t("Offer")}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && <Pagination page={data.page} pageCount={Math.max(1, Math.ceil(data.total / data.pageSize))} total={data.total} pageSize={data.pageSize} onChange={setPage} />}
        <p className="ui-hint active-users-hint">
          {t("Left: paid subscription ended over the period. Nobody is hidden: each person shows where they stand today (unpaid, back with offered days, subscribed again). Disabled accounts, invalid addresses and unsubscribed people are never contacted.")}
        </p>
      </section>

      {composer && (
        <ComebackComposer
          items={composer}
          giftOptions={data?.giftDays}
          cooldownDays={data?.cooldownDays}
          onClose={() => setComposer(null)}
          onSent={() => {
            setComposer(null);
            setSelected([]);
            load();
          }}
        />
      )}
    </Page>
  );
};

export default Comeback;
